import { showDevWarning } from '@umss/shared/utils';

import {
  SubscriptionTier,
  SubscriptionTierBand
} from './subscription.models';

/**
 * Subscription pricing, as the apps display it.
 *
 * ## Why the frontend holds these
 *
 * Pricing has to be readable *before* a facility subscribes — it is the thing
 * they read in order to decide. The API only ships the bands alongside an open
 * billing cycle, which does not exist until there is a subscription, so it
 * cannot answer the question at the moment it is asked.
 *
 * Reading the API where it answers and falling back here where it does not
 * would be worse than either: the two could disagree, and the price would then
 * *change at the moment of subscribing*. So these are the single display
 * source, used whether or not a subscription exists.
 *
 * ## What still belongs to the API
 *
 * Everything that decides money. Invoices snapshot their own `tierPrice`, so a
 * historical invoice never re-prices from here, and the tier a cycle lands in
 * is resolved server-side. These values are shown, never used to compute a
 * charge.
 *
 * ## Keeping them honest
 *
 * They must match `SubscriptionTierResolver` on the backend. Nothing enforces
 * that, so {@link warnOnTierPriceDrift} shouts in development the moment the
 * API sends bands that disagree — see its remarks.
 */
export const SUBSCRIPTION_TIER_BANDS: readonly SubscriptionTierBand[] = [
  {
    tier: SubscriptionTier.Tier1,
    tierDisplay: 'Tier 1',
    lowerBound: 1,
    upperBound: 25,
    shiftRange: '1-25',
    price: 200
  },
  {
    tier: SubscriptionTier.Tier2,
    tierDisplay: 'Tier 2',
    lowerBound: 26,
    upperBound: 50,
    shiftRange: '26-50',
    price: 500
  },
  {
    tier: SubscriptionTier.Tier3,
    tierDisplay: 'Tier 3',
    lowerBound: 51,
    upperBound: 100,
    shiftRange: '51-100',
    price: 750
  },
  {
    tier: SubscriptionTier.Tier4,
    tierDisplay: 'Tier 4',
    lowerBound: 101,
    upperBound: null,
    shiftRange: '101+',
    price: 1000
  }
];

/** Job Postings' flat monthly fee. Displayed the same way, and for the same reason. */
export const JOB_POSTINGS_MONTHLY_PRICE = 100;

/**
 * What to call a band in front of a customer.
 *
 * The API's own `tierDisplay` is the enum name — it arrives as `"Tier1"`,
 * unspaced, because the backend sends `tier.ToString()`. Rendering it directly
 * puts that in a sentence a facility reads. The ladder above is the display
 * source (see the note at the top of this file), so the name comes from there,
 * matched on the tier the API named.
 *
 * Falls back to whatever the API sent if the tier is one this ladder does not
 * know — a new band should read oddly rather than not at all.
 */
export function tierDisplayName(band: SubscriptionTierBand): string {
  return (
    SUBSCRIPTION_TIER_BANDS.find((local) => local.tier === band.tier)
      ?.tierDisplay ?? band.tierDisplay
  );
}

/** Comparable shape of a ladder, ignoring order and display strings. */
function priceShape(bands: readonly SubscriptionTierBand[]): string {
  return [...bands]
    .sort((a, b) => a.lowerBound - b.lowerBound)
    .map((band) => `${band.lowerBound}-${band.upperBound ?? ''}@${band.price}`)
    .join(' ');
}

/**
 * Shouts, in development, when the API's bands disagree with what we display.
 *
 * The one hazard of holding pricing here is that the backend changes it and we
 * do not: a facility would be quoted one price and invoiced another. It has
 * already happened once on this feature — the 1–5 band was added here and never
 * on the backend, and the two disagreed for days before it was reversed.
 *
 * So wherever the API does send bands, they are compared rather than ignored.
 * Divergence becomes a loud warning the first time anyone opens the page with a
 * live subscription, instead of quietly mispricing everyone who has not
 * subscribed yet.
 */
export function warnOnTierPriceDrift(
  bands: readonly SubscriptionTierBand[] | undefined
): void {
  if (!bands?.length) return;

  const served = priceShape(bands);
  const shown = priceShape(SUBSCRIPTION_TIER_BANDS);
  if (served === shown) return;

  showDevWarning(
    'Subscription tier pricing on the API no longer matches what the apps ' +
      'display. Update SUBSCRIPTION_TIER_BANDS — until then, facilities that ' +
      'have not subscribed are being quoted stale prices.',
    { served, shown }
  );
}
